<template lang="pug">
.home-banners-wrap(v-if="bannersVisiveis.length")
  //- ===== 1 banner: largura total, proporção 3:1, sem controles =====
  component.home-banner-single(
    v-if="!carrossel",
    :is="unico.url ? 'a' : 'div'",
    :href="unico.url || null",
    :target="unico.url ? '_blank' : null",
    rel="noopener noreferrer",
    @click="onClick(unico)"
  )
    img.home-banner__img(
      :src="imagemUrl(unico)",
      :alt="unico.titulo || 'Banner'",
      @error="onImgError(unico)"
    )

  //- ===== 2+ banners: carrossel, 2 por vez, passo de 1, loop infinito =====
  .home-banners(v-else, @mouseenter="pausar", @mouseleave="retomar")
    .home-banners__track(:style="trackStyle")
      component.home-banner(
        v-for="(banner, i) in slides",
        :key="`${banner.id}-${i}`",
        :is="banner.url ? 'a' : 'div'",
        :href="banner.url || null",
        :target="banner.url ? '_blank' : null",
        rel="noopener noreferrer",
        @click="onClick(banner)"
      )
        img.home-banner__img(
          :src="imagemUrl(banner)",
          :alt="banner.titulo || 'Banner'",
          @error="onImgError(banner)"
        )

    button.home-banners__arrow.home-banners__arrow--left(
      type="button",
      @click="anterior",
      aria-label="Banner anterior"
    ) &lsaquo;
    button.home-banners__arrow.home-banners__arrow--right(
      type="button",
      @click="proximo",
      aria-label="Próximo banner"
    ) &rsaquo;

    .home-banners__progress(:style="progressStyle")
</template>

<script>
// Duração da transição do track (ver .home-banners__track no <style>).
const DURACAO_TRANSICAO = 600;
// Folga para o reposicionamento do loop acontecer depois da animação terminar.
const ATRASO_LOOP = DURACAO_TRANSICAO + 20;
// Quantos banners aparecem por vez no desktop (define o passo do track).
const CLONES = 2;

export default {
  name: 'HomeBanners',
  props: {
    // Localidade do estabelecimento selecionado — segmenta os banners.
    localidadeId: { type: [Number, String], default: null },
    // Loja selecionada — a API avalia o público do banner sobre os parâmetros dela.
    estabelecimentoId: { type: [Number, String], default: null },
    // Tempo de cada banner na rotação automática, em segundos.
    intervalo: { type: Number, default: 5 },
  },
  data() {
    return {
      banners: [],
      // Posição do track. Pode assumir -1 e n durante o loop (slides clonados).
      pos: 0,
      pausado: false,
      // Progresso do banner atual, em segundos.
      tick: 0,
      // Desliga a transição no instante em que o track volta para o slide real.
      semAnimacao: false,
      // Em telas menores mostramos 1 banner por vez.
      umPorVez: false,
    };
  },
  computed: {
    urlImagem() {
      return process.env.VUE_APP_IMAGE_URL;
    },
    chaveConsulta() {
      return `${this.localidadeId || ''}-${this.estabelecimentoId || ''}`;
    },
    bannersVisiveis() {
      return this.banners.filter(b => b.imagem && !b.imagemQuebrada);
    },
    // Com 1 banner não há o que rotacionar: vira faixa única de largura total.
    carrossel() {
      return this.bannersVisiveis.length > 1;
    },
    unico() {
      return this.bannersVisiveis[0] || {};
    },
    // Clones nas duas pontas dão o loop infinito sem espaço em branco.
    slides() {
      if (!this.carrossel) return [];
      const b = this.bannersVisiveis;
      return [...b.slice(-CLONES), ...b, ...b.slice(0, CLONES)];
    },
    indiceAtivo() {
      const n = this.bannersVisiveis.length;
      if (!n) return 0;
      return ((this.pos % n) + n) % n;
    },
    intervaloSeguro() {
      const iv = Number(this.intervalo);
      if (!iv || Number.isNaN(iv)) return 5;
      return Math.min(12, Math.max(2, iv));
    },
    trackStyle() {
      const passo = this.umPorVez ? '100% + 16px' : '50% + 8px';
      return {
        transform: `translateX(calc(-${this.pos + CLONES} * (${passo})))`,
        transitionDuration: this.semAnimacao ? '0s' : `${DURACAO_TRANSICAO}ms`,
      };
    },
    progressStyle() {
      const pct = this.pausado ? 0 : (this.tick / this.intervaloSeguro) * 100;
      return { width: `${pct}%` };
    },
  },
  watch: {
    // Localidade e loja mudam juntas ao trocar de estabelecimento — uma chave só evita 2 requests.
    chaveConsulta() {
      this.fetch();
    },
    bannersVisiveis(lista) {
      // Um banner com imagem quebrada pode encurtar a lista sob o track.
      if (this.pos >= lista.length || this.pos < 0) this.reposicionar(0);
      this.iniciarTimer();
    },
  },
  mounted() {
    this.fetch();
    this.atualizarBreakpoint();
    window.addEventListener('resize', this.atualizarBreakpoint);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.atualizarBreakpoint);
    this.pararTimer();
    clearTimeout(this.loopTimeout);
  },
  methods: {
    async fetch() {
      try {
        const { data } = await this.$axios.get('/banner', {
          params: {
            localidade_id: this.localidadeId || undefined,
            estabelecimento_id: this.estabelecimentoId || undefined,
          },
        });
        this.banners = Array.isArray(data) ? data : [];
      } catch (e) {
        // Falha ao carregar banners não deve afetar o restante do painel.
        this.banners = [];
      }
      this.reposicionar(0);
      this.iniciarTimer();
    },
    imagemUrl(banner) {
      return `${this.urlImagem}/banners/${banner.imagem}`;
    },
    onImgError(banner) {
      this.$set(banner, 'imagemQuebrada', true);
    },
    onClick(banner) {
      if (!banner.url) return;
      if (this.$firebase && this.$firebase.logEvent) {
        this.$firebase.logEvent('home_banner_clicked', {
          banner_id: banner.id,
          localidade_id: this.localidadeId,
        });
      }
    },
    atualizarBreakpoint() {
      this.umPorVez = window.innerWidth <= 960;
    },
    // Pula para uma posição sem animar (usado no loop e ao trocar a lista).
    reposicionar(pos) {
      clearTimeout(this.loopTimeout);
      this.pos = pos;
      this.tick = 0;
      this.semAnimacao = true;
    },
    avancar(direcao) {
      const n = this.bannersVisiveis.length;
      if (!this.carrossel) return;

      clearTimeout(this.loopTimeout);
      this.pos += direcao;
      this.tick = 0;
      this.semAnimacao = false;

      // Passou de uma das pontas: anima até o clone e volta ao slide real.
      if (this.pos >= n) {
        this.loopTimeout = setTimeout(() => this.reposicionar(0), ATRASO_LOOP);
      } else if (this.pos < 0) {
        this.loopTimeout = setTimeout(() => this.reposicionar(n - 1), ATRASO_LOOP);
      }
    },
    proximo() {
      this.avancar(1);
    },
    anterior() {
      this.avancar(-1);
    },
    irPara(i) {
      clearTimeout(this.loopTimeout);
      this.pos = i;
      this.tick = 0;
      this.semAnimacao = false;
    },
    pausar() {
      this.pausado = true;
    },
    retomar() {
      this.pausado = false;
    },
    iniciarTimer() {
      this.pararTimer();
      if (!this.carrossel) return;
      this.timer = setInterval(() => {
        if (this.pausado) return;
        this.tick = Math.round((this.tick + 0.1) * 10) / 10;
        if (this.tick >= this.intervaloSeguro) this.proximo();
      }, 100);
    },
    pararTimer() {
      clearInterval(this.timer);
      this.timer = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.home-banners-wrap {
  position: relative;
  margin-top: 20px;
}

// ===== Banner único =====
.home-banner-single {
  display: block;
  aspect-ratio: 3 / 1;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
}

// ===== Carrossel =====
.home-banners {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  // 2 banners 3:1 lado a lado
  aspect-ratio: 6 / 1;
}

.home-banners__track {
  display: flex;
  height: 100%;
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.home-banner {
  display: block;
  flex: 0 0 auto;
  min-width: calc(50% - 8px);
  margin-right: 16px;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
}

.home-banner__img {
  display: block;
  width: 100%;
  height: 100%;
  // Banners têm proporção 1200x400 (3:1)
  object-fit: cover;
}

.home-banners__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  color: #2b2f2b;
  font-size: 22px;
  line-height: 1;
  padding: 0 0 3px;
  cursor: pointer;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s;

  &--left {
    left: 14px;
  }

  &--right {
    right: 14px;
  }

  &:hover {
    background: #fff;
  }
}

.home-banners:hover .home-banners__arrow {
  opacity: 1;
}

// Sem hover (toque): setas sempre visíveis
@media (hover: none) {
  .home-banners__arrow {
    opacity: 1;
  }
}

.home-banners__progress {
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;
  height: 3px;
  background: rgba(255, 255, 255, 0.9);
  transition: width 0.1s linear;
}

// ===== Telas menores: 1 banner por vez =====
@media (max-width: 960px) {
  .home-banners {
    aspect-ratio: 3 / 1;
  }

  .home-banner {
    min-width: 100%;
  }
}
</style>
